import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Monitor, Smartphone, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { previewAPI, pagesAPI } from '@/lib/api';

export default function PreviewPage() {
  const { siteId } = useParams();
  const [searchParams] = useSearchParams();
  const [device, setDevice] = useState('desktop');
  const [pages, setPages] = useState([]);
  const [currentSlug, setCurrentSlug] = useState(searchParams.get('slug') || '/');
  const iframeRef = useRef(null);

  useEffect(() => {
    pagesAPI.list(siteId).then(res => setPages(res.data)).catch(() => {});
  }, [siteId]);

  // Listen for postMessage from iframe (template navigation)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'preview-navigate') {
        const newSlug = event.data.slug || '/';
        setCurrentSlug(newSlug);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const previewUrl = previewAPI.getHTML(siteId, currentSlug);

  // When slug selector changes, update iframe
  const handleSlugChange = (newSlug) => {
    setCurrentSlug(newSlug);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/50 bg-background/90 backdrop-blur">
        <div className="flex items-center gap-3">
          <Link to={`/sites/${siteId}/detail`}>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-2 bg-secondary/40 px-3 py-1.5 rounded-lg">
            <div className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="text-xs font-mono text-muted-foreground truncate max-w-[300px]">{currentSlug}</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <ToggleGroup type="single" value={device} onValueChange={(v) => v && setDevice(v)} data-testid="preview-device-toggle">
            <ToggleGroupItem value="desktop" aria-label="Masaustu" data-testid="preview-desktop-button" className="h-8 px-3">
              <Monitor className="w-4 h-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="mobile" aria-label="Mobil" data-testid="preview-mobile-button" className="h-8 px-3">
              <Smartphone className="w-4 h-4" />
            </ToggleGroupItem>
          </ToggleGroup>

          {/* Page selector */}
          <select
            value={currentSlug}
            onChange={(e) => handleSlugChange(e.target.value)}
            className="bg-secondary/40 border border-border/70 rounded-lg px-3 py-1.5 text-xs font-mono text-foreground"
          >
            {pages.length > 0 ? pages.map(p => (
              <option key={p.slug} value={p.slug}>{p.slug} — {(p.seo_title || '').slice(0, 30)}</option>
            )) : (
              <option value="/">/</option>
            )}
          </select>

          <a href={previewUrl} target="_blank" rel="noopener noreferrer">
            <Button variant="ghost" size="sm" className="gap-1.5 text-xs">
              <ExternalLink className="w-3 h-3" /> Yeni Sekmede Ac
            </Button>
          </a>
        </div>
      </div>

      {/* Preview frame */}
      <div className="flex-1 flex items-start justify-center bg-[hsl(220,10%,4%)] overflow-auto p-4">
        <div className={device === 'desktop' ? 'preview-desktop' : 'preview-mobile'} data-testid="preview-iframe">
          <iframe
            ref={iframeRef}
            src={previewUrl}
            title="Site Onizleme"
            className="w-full h-full border-0 bg-white"
            style={{ borderRadius: device === 'mobile' ? '22px' : 0 }}
          />
        </div>
      </div>
    </div>
  );
}
